import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { RightSidebarService } from '../../../../../services/right-sidebar.service';
import { Currency, ICard, IColumn, IContact, IError, IFunnel } from '../../../../../interfaces';
import { Subject, takeUntil } from 'rxjs';
import { CrmService } from '../../../../../services/crm.service';
import { CardForm, contactActions, ContactForm, ContactInfoList } from './card-old.interface';
import { NotificationService } from '../../../../../services/notifications.service';
import { CURRENCY, DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../../../../../enum/constants';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { _filter, parseCurrency } from '../../../../../helpers';
import { ContactService } from '../../../../../services/contact.service';
import { BaseService } from '../../../../../services/base.service';
import { DialogService } from '../../../../../services/dialog.service';
import { CustomAttributesService } from '../../../../../services/custom-attributes.service';
import { ICA } from '../../../../../tabs/tab-deals-settings-fields/tab-deals-settings-fields.interface';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { Tab } from '../../../../tabs/interfaces/tab.interface';
import { CardTab } from '../../../../tabs/enums/tabs.enum';

@Component({
  selector: 'app-card-old-details',
  templateUrl: './card-old.component.html',
  styleUrls: ['./card-old.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CardOldComponent implements OnInit, OnDestroy {
  // Destroys
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Form
  public cardForm!: FormGroup<CardForm>;
  public contactForm!: FormGroup<ContactForm>;
  public cardCAForm!: FormGroup;
  public copyCardForm!: any;
  public copyCardCAForm!: any;

  // Var-s
  protected readonly CURRENCY: Currency[] = CURRENCY;
  protected readonly parseCurrency = parseCurrency;
  protected readonly _filter = _filter;
  protected readonly contactActions = contactActions;

  public cardId!: string;
  public columns: IColumn[] = [];
  public customAttributes: ICA[] = [];

  public contacts: IContact[] = [];
  public funnels: IFunnel[] = [];
  public card!: ICard;
  public funnel!: IFunnel;
  public column!: IColumn;
  public hoveredBgColor: string | null = null;

  public hoveredTextColor: string | null = null;
  public hoveredIndex: number = 0;

  public contactPage: number = 1;
  public searchedContact: string = '';

  public current_id!: any;

  public isContactCreate: boolean = false;
  public isContactUpdate: boolean = false;
  public isContactSelect: boolean = false;

  public isFormSubmitted: boolean = false;
  public isAllDeactivate: boolean = false;

  public isHasChange: boolean = false;
  public isActiveSomeInput: boolean = false;

  public currentColumn!: IColumn | any;
  public currentContact!: IContact | any;

  public contactInfoList: ContactInfoList[] = [];

  private colorTimer!: NodeJS.Timeout;

  public readonly tabs: Tab<CardTab>[] = [
    {
      name: 'general',
      value: CardTab.GENERAL,
      default: true,
    },
  ];
  public selectedTab!: Tab<CardTab>;

  public colors: string[] = [
    '#fbc6bd',
    '#bde2ff',
    '#ffb4ce',
    '#d1ffc3',
    '#fbe9b8',
    '#f8edae',
    '#a3e1ce',
    '#b7eded',
    '#b4b9f3',
    '#ceb6ef',
    '#dfe6e9',
    '#ffffff',
  ];

  @ViewChild('updateInput') updateInput!: ElementRef;

  constructor(
    private readonly rightSidebarService: RightSidebarService,
    private readonly notificationService: NotificationService,
    private readonly crmService: CrmService,
    private readonly contactService: ContactService,
    public readonly baseService: BaseService,
    public readonly dialogService: DialogService,
    public readonly customAttributesService: CustomAttributesService,
    public readonly router: Router,
  ) {
    const options = this.rightSidebarService.getOptions();
    this.cardId = options.data.card_id;
  }

  ngOnInit(): void {
    this.router.navigate([], {
      queryParams: {
        card: this.cardId,
      },
    });
    this.crmService.getColumnCardById(this.cardId).subscribe((res: ICard) => {
      this.card = res;
      this.funnel = res.funnel;
      this.column = res.column;
      if (this.card.funnel_id) {
        this.onInitAllFunnels();
        this.onInitColumnsByFunnel(this.card.funnel_id);
        this.onInitCAByFunnel(this.card.funnel_id);
        // this.onInitCard(this.card?.id);

        if (this.card?.contact_id) {
          this.onInitContact();
        } else {
          this.initContactForm();
        }

        this.initForm();
        this.onCreateGroupFormValueChange();
      } else {
        this.rightSidebarService.close();
        this.notificationService.showError(
          activeTranslations.notifications_texts.error.error,
          activeTranslations.notifications_texts.error.something_went_wrong_1,
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.router.navigate([], {
      queryParams: {
        card: null,
      },
    });
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private onCreateGroupFormValueChange() {
    const initialValueCard = { ...this.cardForm.getRawValue() };
    this.trackFormChanges(this.cardForm, initialValueCard);
  }

  private trackFormChanges(form: FormGroup, initialValue: any): void {
    form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isHasChange = !_.isEqual(value, initialValue);
    });
  }

  private initForm(isCA = false) {
    this.cardForm = new FormGroup<CardForm>({
      name: new FormControl(this.card.name, Validators.required),
      price: new FormControl(this.card.price, Validators.required),
      funnel_id: new FormControl(this.card.funnel_id, Validators.required),
      currency: new FormControl(this.card.currency, Validators.required),
      bg_color: new FormControl(this.card.bg_color, Validators.required),
      tx_color: new FormControl(this.card.tx_color, Validators.required),
      comment: new FormControl(this.card.comment),
    });

    if (!isCA) this.cardCAForm = new FormGroup<any>({});

    this.copyCardForm = { ...this.cardForm.getRawValue() };
  }

  private initContactForm() {
    this.getAllContacts(true);

    if (this.currentContact) {
      this.contactForm = new FormGroup<ContactForm>({
        contact_id: new FormControl(this.currentContact.id),
        name: new FormControl(this.currentContact.name, Validators.required),
        phone: new FormControl(_filter(this.currentContact.phones)[0]),
        email: new FormControl(_filter(this.currentContact.emails)[0], Validators.email),
        address: new FormControl(_filter(this.currentContact.addresses)[0]),
      });
    } else {
      this.contactForm = new FormGroup<ContactForm>({
        contact_id: new FormControl(null),
        name: new FormControl(null, Validators.required),
        phone: new FormControl(null),
        email: new FormControl(null, Validators.email),
        address: new FormControl(null),
      });
    }
  }

  private getAllContacts(isInit: boolean = false, searchTerm: string = '') {
    this.contactService
      .getAll({ search: searchTerm, take: 10, page: this.contactPage })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (isInit || !!searchTerm) this.contacts = res.items;
          else this.contacts = [...this.contacts, ...res.items];

          if (this.currentContact && !this.contacts.some((obj) => _.isEqual(obj, this.currentContact)))
            this.contacts.unshift(this.currentContact);
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public onInitColumnsByFunnel(funnel_id: string): void {
    this.crmService
      .getAllColumns({
        funnel_id: funnel_id,
        page: 1,
        take: 50,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resColumn) => {
          if (!resColumn.items) return;
          this.columns = [...resColumn.items];
          this.currentColumn = this.columns.find((v: IColumn): boolean => v.id === this.card.column_id);
        },
      });
  }

  public onInitCAByFunnel(funnel_id: string): void {
    this.customAttributesService
      .getAllCA({
        funnel_id: funnel_id,
        page: 1,
        take: 50,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resCA) => {
          if (!resCA.items) return;
          this.customAttributes = [...resCA.items].map((v) => ({ ...v, is_active: false }));
          for (const ca of this.customAttributes) {
            this.cardCAForm.addControl(ca.key, new FormControl(this.card.custom_attributes?.[ca.key] || ''));
          }
          this.copyCardCAForm = { ...this.cardCAForm.getRawValue() };

          const initialValueCA = { ...this.cardCAForm.getRawValue() };
          this.trackFormChanges(this.cardCAForm, initialValueCA);
        },
      });
  }

  public onInitAllFunnels(): void {
    this.crmService
      .getAllFunnels({ type: this.funnel.type })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (funnels: IFunnel[]) => {
          this.funnels = [...funnels];
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public onInitContact(): void {
    this.contactService
      .getById(this.card.contact_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.currentContact = res;
          this.contactInfoList = [
            {
              icon: 'phone',
              items: this.currentContact?.phones,
            },
            {
              icon: 'mail',
              items: this.currentContact?.emails,
            },
            {
              icon: 'location_on',
              items: this.currentContact?.addresses,
            },
          ];
          this.initContactForm();
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public isHoverArrow(hover: boolean, columnBgColor: string, columnTextColor: string, index: number): void {
    this.hoveredIndex = hover ? index : 0;
    this.hoveredBgColor = hover ? columnBgColor : null;
    this.hoveredTextColor = hover ? columnTextColor : null;
  }

  public onGetColumnColor(column: any, i: number, colorType: string) {
    const targetColumn: any = this.columns.find((v: IColumn) => v.id === this.card.column_id);
    if (!targetColumn) return;

    const targetColor = colorType === 'bg' ? targetColumn.bg_color : targetColumn.text_color;
    const hoveredColor = colorType === 'bg' ? this.hoveredBgColor : this.hoveredTextColor;

    if (!hoveredColor && i <= this.columns.indexOf(this.currentColumn)) return targetColor;
    else if (hoveredColor && !!`${this.hoveredIndex}` && i <= this.hoveredIndex) return hoveredColor;
  }

  public onSaveUpdate(): void {
    this.isFormSubmitted = true;
    const payload = this.cardForm.getRawValue();

    const isEqual = _.isEqual(this.copyCardForm, payload);

    if (this.cardForm.valid && !isEqual) {
      this.crmService
        .updateCard(this.card.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: ICard) => {
            this.card = res;
            this.copyCardForm = { ...payload };
          },
          error: (e): void => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
            });
          },
        });
    }
  }

  public onUpdate(): void {
    this.isFormSubmitted = true;

    const cardName = this.cardForm.controls.name.value.trim();
    const payload: any = this.cardForm.getRawValue();
    const payloadCA = this.cardCAForm.getRawValue();

    if (cardName === '') {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.name_card_empty,
      );
    } else {
      const isEqual = _.isEqual(this.copyCardForm, payload);
      const isEqualCA = _.isEqual(this.copyCardCAForm, payloadCA);

      if ((this.cardForm.valid && !isEqual) || (this.cardCAForm.valid && !isEqualCA)) {
        this.isAllDeactivate = !this.isAllDeactivate;
        this.isActiveSomeInput = false;
      }

      if (this.cardForm.valid && !isEqual) {
        const newPayload: Record<string, string> = {};
        for (const key in payload) {
          if (!_.isEqual(this.copyCardForm[key], payload[key])) {
            newPayload[key] = payload[key];
          }
        }
        this.crmService
          .updateCard(this.card.id, payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: ICard) => {
              this.card = res;
              this.initForm(true);
              this.onCreateGroupFormValueChange();

              this.isHasChange = false;
            },
            error: (e): void => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      }

      if (this.cardCAForm.valid && !isEqualCA) {
        const newPayloadCA: Record<string, string> = {};
        for (const key in payloadCA) {
          if (!_.isEqual(this.copyCardCAForm[key], payloadCA[key])) {
            newPayloadCA[key] = payloadCA[key];
          }
        }
        this.crmService
          .updateColumnCardCABulk(newPayloadCA, this.card.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.customAttributes = this.customAttributes.map((v) => ({ ...v, is_active: false }));
              this.customAttributes.forEach((v) => (v.is_active = false));
              this.isHasChange = false;

              const initialValueCA = { ...this.cardCAForm.getRawValue() };
              this.trackFormChanges(this.cardCAForm, initialValueCA);
              // this.card-old = res;
              // this.copyCardForm = { ...payload };
            },
            error: (e): void => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      }
    }
  }

  public onCancelUpdate(): void {
    this.isAllDeactivate = !this.isAllDeactivate;
    this.isActiveSomeInput = false;

    this.cardForm.reset(this.card);

    this.cardCAForm.reset(this.copyCardCAForm);

    const copyCA = [...this.customAttributes];
    this.customAttributes = [];
    this.customAttributes = [...copyCA];
    this.card.custom_attributes = { ...this.card.custom_attributes };
  }

  public onChangeColumn(column_id: any): void {
    if (column_id === this.current_id) {
      return;
    }

    this.current_id = column_id;

    this.crmService
      .updateCard(this.card.id, { column_id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ICard) => {
          this.card = {} as ICard;
          this.card = res;
          this.currentColumn = this.columns.find((v: IColumn) => v.id === this.card.column_id);
        },
        error: (e): void => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public onChangeTab(selectedTab: any): void {
    this.selectedTab = selectedTab;
  }

  public onChangeFunnel(e: any): void {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.deal_1',
    });
    dialogRef.subscribe((result: any) => {
      if (result) {
        this.crmService
          .updateCard(this.card.id, { funnel_id: e.id })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: ICard) => {
              this.card = {} as ICard;
              this.card = res;
              this.onInitColumnsByFunnel(this.card.funnel_id);
            },
            error: (e): void => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      }
    });
  }

  public columnStyle(column: IColumn, i: number): Record<string, string> {
    const bgColor = this.onGetColumnColor(column, i, 'bg');
    const txtColor = this.onGetColumnColor(column, i, 'txt');

    return { '--column-bg-color': bgColor, '--column-text-color': txtColor };
  }

  public onChangeContact(contact: any) {
    this.contactForm.setValue({
      contact_id: contact.id,
      name: contact.name,
      phone: _filter(contact?.phones)[0] || null,
      email: _filter(contact?.emails)[0] || null,
      address: _filter(contact?.addresses)[0] || null,
    });
  }

  private isValid(): boolean {
    let isValid: boolean = true;

    if (this.contactForm.controls.name.invalid) {
      this.contactForm.controls.name.markAsTouched();
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.name_field_required,
      );

      isValid = false;
    }

    if (this.contactForm.controls.email.invalid) {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.email_not_expected,
      );
      isValid = false;
    }

    if (!!this.contactForm.getRawValue()?.phone?.length && this.contactForm.getRawValue()?.phone?.length < 12) {
      this.contactForm.controls.phone.setErrors({
        incorrect: true,
      });

      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.number_invalid,
      );
      isValid = false;
    }

    return isValid;
  }

  public onSaveContact() {
    const { contact_id, name, phone, email, address } = this.contactForm.getRawValue();

    if (this.contactForm.valid && this.isValid()) {
      if (this.isContactCreate) {
        this.contactService
          .create({
            name,
            phone: Array.isArray(phone) ? phone.filter(Boolean) : phone !== null ? [phone] : [],
            email: Array.isArray(email) ? email.filter(Boolean) : email !== null ? [email] : [],
            address: Array.isArray(address) ? address.filter(Boolean) : address !== null ? [address] : [],
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              this.crmService
                .updateCard(this.card.id, { contact_id: res.id })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (r) => {
                    this.card = {} as ICard;
                    this.card = r;

                    this.onInitContact();
                    this.getAllContacts(true);

                    this.searchedContact = '';
                    this.contactPage = 1;

                    this.isContactCreate = false;
                    this.isContactSelect = false;
                    this.isContactUpdate = false;
                  },
                  error: (e) => {
                    (e.error?.errors || []).forEach((err: IError) => {
                      this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
                    });
                  },
                });
            },
            error: (e): void => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      } else if (this.card.contact_id === contact_id && this.isContactUpdate) {
        this.contactService
          .update(this.currentContact.id, {
            name,
            phone: Array.isArray(phone) ? phone.filter(Boolean) : phone !== null ? [phone] : [],
            email: Array.isArray(email) ? email.filter(Boolean) : email !== null ? [email] : [],
            address: Array.isArray(address) ? address.filter(Boolean) : address !== null ? [address] : [],
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              this.currentContact = res;

              this.onInitContact();

              this.isContactUpdate = false;
            },
            error: (e) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      } else if (this.card.contact_id !== contact_id) {
        this.crmService
          .updateCard(this.card.id, { contact_id: contact_id })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (r) => {
              this.card = {} as ICard;
              this.card = r;

              this.onInitContact();

              this.isContactCreate = false;
              this.isContactSelect = false;
              this.isContactUpdate = false;
            },
            error: (e) => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      }
    }
  }

  public onCloseContact(isBack?: boolean) {
    if (this.contactForm && this.currentContact) {
      this.contactForm.reset({
        contact_id: this.currentContact.id,
        name: this.currentContact.name,
        phone: _filter(this.currentContact.phones)[0],
        email: _filter(this.currentContact.emails)[0],
        address: _filter(this.currentContact.addresses)[0],
      });
    }

    if (isBack) this.isContactCreate = false;
    else {
      if (this.isContactUpdate) {
        this.isContactUpdate = false;
      } else {
        this.isContactSelect = false;
      }
    }
  }

  public onDeleteContact() {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.deal_2',
    });
    dialogRef.subscribe((result: any) => {
      if (result) {
        this.crmService
          .updateCard(this.card.id, { contact_id: null })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: ICard) => {
              this.card = res;
              this.currentContact = null;
            },
            error: (e): void => {
              (e.error?.errors || []).forEach((err: IError) => {
                this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
              });
            },
          });
      }
    });
  }

  private onInitCard(id: string) {
    this.crmService.getColumnCardById(id).subscribe({
      next: (res: ICard) => {
        this.card = res;
      },
      error: (e): void => {
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });
      },
    });
  }

  public onCreateContactForm() {
    this.isContactCreate = true;
    if (this.contactForm) this.contactForm.reset();
  }

  public onContactChanges(isSelect: boolean = false, isUpdate: boolean = false) {
    if (isSelect) this.isContactSelect = true;
    if (isUpdate) this.isContactUpdate = true;
  }

  public onSaveUpdateCA(key: string, value: any) {
    const payload = {
      key,
      value,
    };
    this.crmService
      .updateColumnCardCA(payload, this.card.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ICard) => {
          this.card = res;
        },
        error: (e): void => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  public scrollToEndContacts() {
    ++this.contactPage;
    this.getAllContacts(false, this.searchedContact);
  }

  public searchContact(e: any): void {
    this.searchedContact = e.term;
    this.getAllContacts(false, this.searchedContact);
  }

  public onCloseDropdown() {
    this.searchedContact = '';
    this.contactPage = 1;
    this.getAllContacts();
  }

  public onActiveSomeInput() {
    this.isActiveSomeInput = true;
  }

  // onUpdateColor() {
  //   if (this.colorTimer) clearTimeout(this.colorTimer);
  //   this.colorTimer = setTimeout(() => {
  //     this.onUpdate();
  //   }, 3000);
  // }
}
